import clsx from 'clsx';
import { useCallback } from 'react';

import { FormElementText } from '~components/generic/editor/FormElementText';
import useAppSelector from '~hooks/useAppSelector';
import useLabels from '~hooks/useLabels';
import { IEntityComponentProps } from '~interfaces/props';
import MediaGalleryController from '~modules/media-gallery/controller/MediaGalleryController';
import { MediaGalleryConfig } from '~modules/media-gallery/model/Config';
import { getModuleUrlIdFromData } from '~services/location-helpers';
import { selectConfigDefaultMediaLibraryRealm } from '~store/selectors';
import MediaGalleryConfigFactory from './model/ConfigFactory';

export const ModuleMediaGallery = ({ data, editing, className, onChange }: IEntityComponentProps) => {
  const defaultRealm = useAppSelector(selectConfigDefaultMediaLibraryRealm);

  const {
    hlMediaGallery,
    msgModuleAttributeMediaLibraryRealmHelp,
    placeholderModuleAttributeMediaLibraryRealm,
    labelModuleAttributeMediaLibraryRealm,
    msgModuleAttributeMediaLibraryQueryHelp,
    placeholderModuleAttributeMediaLibraryQuery,
    labelModuleAttributeMediaLibraryQuery,
  } = useLabels([
    'hlMediaGallery',
    'msgModuleAttributeMediaLibraryRealmHelp',
    'placeholderModuleAttributeMediaLibraryRealm',
    'labelModuleAttributeMediaLibraryRealm',
    'msgModuleAttributeMediaLibraryQueryHelp',
    'placeholderModuleAttributeMediaLibraryQuery',
    'labelModuleAttributeMediaLibraryQuery',
  ]);

  const handleOnChange = useCallback(
    (config: MediaGalleryConfig) => (attr: keyof typeof config) => (value: typeof config[typeof attr]) => {
      const changedConfig = { ...config, [attr]: value };
      onChange('attributes.field_configuration', JSON.stringify(changedConfig));
    },
    [onChange],
  );

  // Do not render if there is no data!
  if (!data) {
    return null;
  }

  // Get field values
  const fieldConfigValue: string = data.attributes.field_configuration || '';
  const mediaGalleryConfig = MediaGalleryConfigFactory(fieldConfigValue);

  const handleConfigFieldChange = handleOnChange(mediaGalleryConfig);

  // derived values
  const { realm: configRealm, query } = mediaGalleryConfig;
  const realm = configRealm || defaultRealm;

  // ClassName generation
  const clsModule = clsx({ ...className.module });
  const clsContainer = clsx({ ...className.container });

  return (
    <div className={clsModule} id={getModuleUrlIdFromData(data.id, data.type)}>
      {editing ? (
        <div className={clsContainer}>
          <div className="row c-module__header">
            <div className="col u-margin-bottom-md container px-0">
              <div className="row">
                <h2>{hlMediaGallery}</h2>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-12">
                  <FormElementText
                    value={realm}
                    onChange={handleConfigFieldChange('realm')}
                    label={labelModuleAttributeMediaLibraryRealm}
                    placeholder={placeholderModuleAttributeMediaLibraryRealm}
                    helpText={msgModuleAttributeMediaLibraryRealmHelp}
                  />
                </div>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-12">
                  <FormElementText
                    value={query}
                    onChange={handleConfigFieldChange('query')}
                    label={labelModuleAttributeMediaLibraryQuery}
                    placeholder={placeholderModuleAttributeMediaLibraryQuery}
                    helpText={msgModuleAttributeMediaLibraryQueryHelp}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <article className="block relative min-h-screen w-full">
          <MediaGalleryController realm={realm} query={query} />
        </article>
      )}
    </div>
  );
};

export default ModuleMediaGallery;
